import React, { FC, ReactNode } from "react";
import { TimesEntry } from "../../Helpers/interfaces";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { nb } from "date-fns/locale";
import { AppointmentDto } from "../../../../../Api/Model/AppointmentDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { ServiceContainerDto } from "../../../../../Api/Model/ServiceContainerDto.csharp";
import { format, startOfDay } from "date-fns";
import { RelatedServiceSetup } from "@apiModels/RelatedServiceSetup.csharp";
import { findRelatedSetupForAppointment } from "../../Helpers/relatedServiceHelper";
import { HourPickerItem } from "./HourPickerItem";
import classNames from "classnames";

interface HourPickerSectionProps extends TimesEntry {
	selectedAppointment?: AppointmentDto;
	doctors: DoctorDto[];
	centers: LocationDto[];
	nextAvailableDateLabel: string;
	goToNextAvailableDay: (date: Date) => void;
	onSelect: (appointment: AppointmentDto) => void;
	selectedDoctor: DoctorDto;
	selectedLocation: LocationDto;
	selectedService: ServiceContainerDto;
	relatedServiceSetups: RelatedServiceSetup[];
}

export const isActive = (
	appointment: AppointmentDto,
	selectedAppointment?: AppointmentDto
) => {
	return (
		appointment.startTime.getTime() ==
			selectedAppointment?.startTime?.getTime() &&
		appointment.doctorId == selectedAppointment?.doctorId &&
		appointment.serviceId == selectedAppointment?.serviceId
	);
};

export const HourPickerSection: FC<HourPickerSectionProps> = ({
	appointments,
	label,
	nextAvailableDate,
	nextAvailableDateLabel,
	selectedAppointment,
	doctors,
	centers,
	goToNextAvailableDay,
	onSelect,
	selectedDoctor,
	selectedLocation,
	selectedService,
	relatedServiceSetups,
}) => {
	const mapDates = (appointment: AppointmentDto, index: number): ReactNode => {
		const active =
			appointment.startTime.getTime() ==
				selectedAppointment?.startTime?.getTime() &&
			appointment.doctorId == selectedAppointment?.doctorId &&
			appointment.serviceId == selectedAppointment?.serviceId;

		const [appointmentDoctor] = doctors!.filter(
			(doctor) =>
				doctor.isNotADoctor == false &&
				doctor.id == selectedAppointment!.doctorId
		);
		const [appointmentCenter] = centers.filter(
			(l) => l.id == selectedAppointment?.centerId
		);

		const relatedSetup = findRelatedSetupForAppointment(
			relatedServiceSetups,
			appointment
		);

		const showDetailsForRelated =
			appointment.isRelatedAppointment &&
			!(
				relatedSetup.relatedServiceLocationSetup
					?.applyLocationAndDoctorFilter ??
				relatedSetup?.relatedServiceSetup?.applyLocationAndDoctorFilter
			);

		const displayCenterDetails =
			(!selectedLocation.id ||
				selectedLocation.id == "-1" ||
				showDetailsForRelated ||
				selectedLocation.isLocation) &&
			appointmentCenter;
		const displayDoctorDetails =
			(!selectedDoctor.id ||
				selectedDoctor.id == "-1" ||
				showDetailsForRelated) &&
			appointmentDoctor;
		const displayAdditionalInfo =
			active && (displayCenterDetails || displayDoctorDetails);

		const itemWrapperRowsCount =
			(displayDoctorDetails
				? selectedService.showDoctorsJobTitle
					? 2
					: 1
				: 0) + (displayCenterDetails ? 1 : 0);

		const itemWrapperClassNames = classNames("HourPickerSection__itemWrapper", {
			"HourPickerSection__itemWrapper--withOneRowAdditionalInfo":
				itemWrapperRowsCount === 1 && displayAdditionalInfo,
			"HourPickerSection__itemWrapper--withTwoRowsAdditionalInfo":
				itemWrapperRowsCount === 2 && displayAdditionalInfo,
			"HourPickerSection__itemWrapper--withThreeRowsAdditionalInfo":
				itemWrapperRowsCount === 3 && displayAdditionalInfo,
		});

		return (
			<li key={`${appointment}-${index}`} className={itemWrapperClassNames}>
				<HourPickerItem
					appointment={appointment}
					onSelect={onSelect}
					active={isActive(appointment, selectedAppointment)}
				/>
				{displayAdditionalInfo && (
					<div className="HourPickerSection__additionalInfo">
						{displayDoctorDetails && (
							<div>
								<span className="HourPickerSection__additionalInfoRow">
									{appointmentDoctor.name}
								</span>
								{selectedService.showDoctorsJobTitle && (
									<span className="HourPickerSection__additionalInfoRow HourPickerSection__additionalInfoRow--doctorJobTitle">
										{appointmentDoctor.jobTitle}
									</span>
								)}
							</div>
						)}
						{displayCenterDetails && (
							<span className="HourPickerSection__additionalInfoRow">
								{appointmentCenter.name}
							</span>
						)}
					</div>
				)}
			</li>
		);
	};

	const getNextAvailableLabel = () => {
		const index = nextAvailableDateLabel.indexOf("$date");
		const firstPart = nextAvailableDateLabel.substring(0, index);
		const secondPart = nextAvailableDateLabel.substring(index + 6);
		const date = startOfDay(nextAvailableDate!);

		return (
			<>
				{firstPart}
				<button
					className="HourPickerSection__noResults__button"
					onClick={() => goToNextAvailableDay(date)}
					type="button"
				>
					{format(date, "EEEE dd. MMMM", { locale: nb })}
				</button>
				{secondPart}
			</>
		);
	};

	if (appointments.length == 0 && !nextAvailableDate) return null;

	return (
		<div className="HourPickerSection">
			<h3 className="HourPickerSection__title">{label}</h3>
			{appointments.length != 0 ? (
				<div>
					<ul className="HourPickerSection__list">
						{appointments.map(mapDates)}
					</ul>
				</div>
			) : (
				nextAvailableDate && (
					<p className="HourPickerSection__noResults">
						{getNextAvailableLabel()}
					</p>
				)
			)}
		</div>
	);
};
